import { Fragment } from 'react';
import { Helmet } from 'react-helmet';
import Image from 'components/Image';
import { useScrollRestore } from 'hooks';
import Footer from 'components/Footer';
import {
  ProjectContainer,
  ProjectSection,
  ProjectSectionContent,
  ProjectImage,
  ProjectTextRow,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectBackground,
  ProjectHeader,
  ProjectSectionColumns,
} from 'components/ProjectLayout';
import ishopify from 'assets/ishopify-homepage.png';
import sliceAppPlaceholder from 'assets/slice-app-placeholder.jpg';
import ishopifySidebarLayers from 'assets/ishopify-sidebar-layers.png';
import ishopifySidebarLayersLarge from 'assets/ishopify-sidebar-layers-large.png';
import sliceSidebarLayersPlaceholder from 'assets/slice-sidebar-layers-placeholder.png';
import ishopifyAdmin from 'assets/ishopify-admin-dashboard.PNG';
import sliceSidebarAnnotationsPlaceholder from 'assets/slice-sidebar-annotations-placeholder.png';
import sliceSlidesPlaceholder from 'assets/slice-slides-placeholder.jpg';
import ishopifyBackgroundBar from 'assets/ishopify-admin-dashboard-mobile.png';
import sliceBackgroundBarPlaceholder from 'assets/slice-background-bar-placeholder.jpg';
import prerender from 'utils/prerender';
import { media } from 'utils/style';
import './index.css';

const title = 'ishopify';
const description =
  'This project involved designing a better way for biomedical educators and learners to annotate digital slides together.';
const roles = ['MERN', 'Ecommerce', 'Redux'];

const Ishopify = () => {
  useScrollRestore();
  return (
    <Fragment>
      <Helmet>
        <title>{`Projects | ${title}`}</title>
        <meta name="description" content={description} />
      </Helmet>
      <ProjectContainer className="slice">
        <ProjectBackground
          srcSet={`${ishopify} 1280w, ${ishopify} 2560w`}
          placeholder={ishopify}
          opacity={0.8}
          entered={!prerender}
        />
        <ProjectHeader
          title={title}
          description={description}
          url="https://github.com/iamausafhussain/mern-ecommerce-application-web"
          roles={roles}
        />
        <ProjectSection first>
          <ProjectSectionContent>
            <ProjectImage
              srcSet={`${ishopify} 800w, ${ishopify} 1920w`}
              placeholder={sliceAppPlaceholder}
              alt="The Slice web application showing a selected user annotation."
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 90vw, 80vw`}
            />
          </ProjectSectionContent>
        </ProjectSection>

        <ProjectSection>
          <ProjectSectionColumns centered className="slice__columns">
            <div className="sidebar__images-text">
              <ProjectSectionHeading>Bringing it together</ProjectSectionHeading>
              <ProjectSectionText>
                This is an Ecommerce store where you can buy your attires and style your
                life.
              </ProjectSectionText>
              <ProjectSectionText>
                This Website is mobile responsive allowing both mobile and tablet.
              </ProjectSectionText>
            </div>
            <div className="slice__sidebar-images">
              <Image
                className="slice__sidebar-image"
                srcSet={`${ishopifySidebarLayers} 300w, ${ishopifySidebarLayers} 700w`}
                placeholder={sliceSidebarLayersPlaceholder}
                alt="The layers sidebar design, now with user profiles."
                sizes={`(max-width: ${media.mobile}px) 200px, 343px`}
              />
              <Image
                className="slice__sidebar-image"
                srcSet={`${ishopifySidebarLayersLarge} 300w, ${ishopifySidebarLayersLarge} 700w`}
                placeholder={sliceSidebarAnnotationsPlaceholder}
                alt="Multiple user annotations on a shared layer."
                sizes={`(max-width: ${media.mobile}px) 200px, 343px`}
              />
            </div>
          </ProjectSectionColumns>
        </ProjectSection>

        <ProjectSection light>
          <ProjectSectionContent>
            <ProjectTextRow>
              <ProjectSectionHeading>Improving the experience</ProjectSectionHeading>
              <ProjectSectionText>
                A problem we heard about often form users was that it was difficult to
                find products they had previously seen. To solve this we added a new
                website that lists all annotated products. In addition, we added the
                ability to favorite products, so if users find an interesting product they
                want to annotate later, they can easily save it to their profile.
              </ProjectSectionText>
            </ProjectTextRow>
            <Image
              srcSet={`${ishopifyAdmin} 800w, ${ishopifyAdmin} 1440w`}
              placeholder={sliceSlidesPlaceholder}
              alt="The new My Slides tab in slice, showing annotated and favorited slides."
              sizes={`(max-width: ${media.mobile}px) 500px, (max-width: ${media.tablet}px) 800px, 1000px`}
            />
          </ProjectSectionContent>
        </ProjectSection>
        <ProjectSection>
          <ProjectSectionContent className="slice__grid">
            <div className="slice__grid-image">
              <div className="slice__grid-background">
                <Image
                  srcSet={`${ishopifyBackgroundBar} 400w, ${ishopifyBackgroundBar} 898w`}
                  placeholder={sliceBackgroundBarPlaceholder}
                  alt=""
                  role="presentation"
                  sizes={`(max-width: ${media.mobile}px) 312px, (max-width: ${media.tablet}px) 408px, 514px`}
                />
              </div>
            </div>
            <div className="slice__grid-text">
              <ProjectSectionHeading>Meaningful details</ProjectSectionHeading>
              <ProjectSectionText>
                Marking and annotating areas on ecommerce is the core experience of the
                application. Adding measurements for the website an user needs and area of
                an annotation both helped to communicate the overall scale of the product
                and how large the annotated feature is in comparison.
              </ProjectSectionText>
            </div>
          </ProjectSectionContent>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
};

export default Ishopify;
